import {ChangeDetectionStrategy, Component, computed, Injectable, input, Signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {filter, from, Observable, switchMap} from "rxjs";
import {toObservable, toSignal} from "@angular/core/rxjs-interop";
import {GruppeId, KorellertGruppertSpørreundersøkelseResultatModell, SpørreundersøkelseEnkelModell, SpørreundersøkelseId, SpørreundersøkelseRessursClient, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {TranslateModule} from "@ngx-translate/core";
import {avsluttedeUndersøkelser, ikkePågåendeUndersøkelser, pågåendeUndersøkelser} from "felles-ng2/modell/spørreundersøkelseModellhjelpere";
import {SpørreundersøkelseKorellertResultatlisteKomponent} from "../spørreundersøkelse-korellert-resultatliste/spørreundersøkelse-korellert-resultatliste";
import {map} from "rxjs/operators";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {SpørreundersøkelseListevisningKomponent} from "../spørreundersøkelse-listevisning/spørreundersøkelse-listevisning";
import {MatIconButton} from "@angular/material/button";
import {SpørreundersøkelseOpprettDialogKomponent} from "../../modale/spørreundersøkelse-opprett-dialog/spørreundersøkelse-opprett-dialog";
import {MatDialog} from "@angular/material/dialog";
import {MatIcon} from "@angular/material/icon";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {SpørreundersøkelseDatatjeneste} from "felles-ng2/tjenester/datalager/SpørreundersøkelseDatatjeneste";
import {distinctUntilArrayItemChanged} from "@ngneat/elf";
import {RammeInnholdKomponent, RammeMedTittelKomponent, RammeTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {i18n} from "felles-ng2/tjenester/i18n/Oversettelser";

const debug = initDebugger('SpoerreundersoekelseListeKomponent');

/**
 * Lister spørreundersøkelser knyttet til en gruppe
 */
@Component({
    selector: 'spoerreundersoekelse-liste',
    templateUrl: 'spørreundersøkelse-liste.html',
    styleUrl: 'spørreundersøkelse-liste.scss',
    imports: [
        TranslateModule,
        SpørreundersøkelseKorellertResultatlisteKomponent,
        SpørreundersøkelseListevisningKomponent,
        MatIcon,
        MatIconButton,
        TipsKomponent,
        RammeMedTittelKomponent,
        RammeTittelKomponent,
        RammeInnholdKomponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseListeKomponent {
    static ajsSelector = 'spoerreundersoekelseListe';

    gruppeId = input.required<GruppeId>();

    private undersøkelser!: Signal<SpørreundersøkelseEnkelModell[]>;
    senesteUndersoekelseResultater!: Signal<KorellertGruppertSpørreundersøkelseResultatModell | null>;
    paagaaende = computed(() => pågåendeUndersøkelser(this.undersøkelser()));
    ikkePaagaaende = computed(() => ikkePågåendeUndersøkelser(this.undersøkelser()));
    lukkede = computed(() => avsluttedeUndersøkelser(this.undersøkelser()));
    brukerKanRedigere = computed(() => this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppeId()));
    lasterData = asynkArbeidPågårSignal();

    constructor(private avhengigheter: SpørreundersøkelseListeKomponentAvhengigheter, private kontekst: BrukerkontekstTilbyder, private dialog: MatDialog) {
        this.lastData();
    }

    opprettNy() {
        SpørreundersøkelseOpprettDialogKomponent.vis(this.dialog, this.gruppeId());
    }

    //private hjelpemetoder

    private lastData() {
        this.undersøkelser = toSignal(toObservable(this.gruppeId).pipe(
            switchMap(gruppeId => this.lasterData.startArbeidAvsluttVedLeveranse(this.avhengigheter.finn(gruppeId)))),
          {initialValue: []});

        this.senesteUndersoekelseResultater = toSignal(toObservable(this.ikkePaagaaende).pipe(
            map(undersøkelser => undersøkelser.map(undersøkelse => undersøkelse.id)),
            distinctUntilArrayItemChanged(),
            map(undersøkelsesIder => første(undersøkelsesIder)),
            filter(førsteUndersøkelseId => erIkkeNil(førsteUndersøkelseId)),
            switchMap(førsteUndersøkelseId =>
              this.lasterData.startArbeidAvsluttVedLeveranse(from(this.avhengigheter.lastResultater(førsteUndersøkelseId))))),
          {initialValue: null});
    }
}

@Injectable()
export class SpørreundersøkelseListeKomponentAvhengigheter {

    constructor(private spørreundersøkelseRessurs: SpørreundersøkelseRessursClient, private spørreundersøkelseDatatjeneste: SpørreundersøkelseDatatjeneste) {
    }

    finn(gruppeId: GruppeId): Observable<SpørreundersøkelseEnkelModell[]> {
        return this.spørreundersøkelseDatatjeneste.finnTilknyttetGruppe(gruppeId);
    }

    /** Laster resultater for angitt undersøkelse, ved feil vises feilmelding og null returneres */
    lastResultater(undersøkelseId: SpørreundersøkelseId): Promise<KorellertGruppertSpørreundersøkelseResultatModell | null> {
        return this.spørreundersøkelseRessurs.hentKorrelertGruppertResultat(undersøkelseId)
          .somPromiseVisMeldingVedFeilOgReturnerVerdi(
            (feil, oversettelser) => oversettelser.oversett(i18n('Kunne ikke hente resultater fra spørreundersøkelsen')),
            null
          );
    }
}
